import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { US_STATES } from "@/constants/usStates";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EditPolicyDialog({ policy, open, onOpenChange, onSave, userInfo }) {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Dropdown options from APIs
  const [contactSources, setContactSources] = useState([]);
  const [contactTypes, setContactTypes] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  
  // Dropdown open states for searchable selects
  const [sourceOpen, setSourceOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const [carrierOpen, setCarrierOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [stateOpen, setStateOpen] = useState(false);
  
  // Conditional field visibility
  const [showCustomSource, setShowCustomSource] = useState(false);
  const [showCustomType, setShowCustomType] = useState(false);
  const [showCustomCarrier, setShowCustomCarrier] = useState(false);
  const [showPendingFollowUp, setShowPendingFollowUp] = useState(false);

  useEffect(() => {
    if (policy && open) {
      // Initialize form data with both IDs and names for dropdowns
      setFormData({
        ...policy,
        // Use the ID fields from API response for dropdown prefill
        contact_source_id: policy.contactSource_id || policy.contact_source_id,
        contact_source_name: policy.contactSource || policy.contact_source_name,
        contact_type_id: policy.contactType_id || policy.contact_type_id,
        contact_type_name: policy.contactType || policy.contact_type_name,
        policy_carrier_id: policy.policyCarrier_id || policy.policy_carrier_id,
        carrier_name: policy.policyCarrier || policy.carrier_name,
        policy_status_id: policy.policyStatus_id || policy.policy_status_id,
        status_name: policy.policyStatus || policy.status_name,
      });
      loadDropdownOptions();
      checkConditionalFields(policy);
    }
  }, [policy, open]);

  useEffect(() => {
    // Auto-calculate annual premium
    if (formData.monthly_premium) {
      const annual = parseFloat(formData.monthly_premium) * 12;
      setFormData(prev => ({ ...prev, annual_premium: annual.toFixed(2) }));
    }
  }, [formData.monthly_premium]);

  const loadDropdownOptions = async () => {
    try {
      setLoading(true);
      const [sourcesRes, typesRes, carriersRes, statusesRes] = await Promise.all([
        axios.get(`${API}/meta/pms/source`),
        axios.get(`${API}/meta/pms/type`),
        axios.get(`${API}/meta/pms/carrier`),
        axios.get(`${API}/meta/pms/status`)
      ]);
      
      setContactSources(Array.isArray(sourcesRes.data) ? sourcesRes.data : []);
      setContactTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
      setCarriers(Array.isArray(carriersRes.data) ? carriersRes.data : []);
      
      // Status API uses 'status' field instead of 'name', normalize it
      const normalizedStatuses = Array.isArray(statusesRes.data) 
        ? statusesRes.data.map(s => ({ ...s, name: s.status || s.name }))
        : [];
      setStatuses(normalizedStatuses);
      setLoading(false);
    } catch (err) {
      console.error('Error loading dropdown options:', err);
      toast.error('Failed to load form options');
      setLoading(false);
    }
  };

  const checkConditionalFields = (data) => {
    // Check if Custom Source should be shown
    const sourceId = data.contactSource_id || data.contact_source_id;
    const sourceObj = contactSources.find(s => s.id === sourceId);
    setShowCustomSource(sourceObj?.name === 'Other' || data.custom_source);
    
    // Check if Custom Type should be shown
    const typeId = data.contactType_id || data.contact_type_id;
    const typeObj = contactTypes.find(t => t.id === typeId);
    setShowCustomType(typeObj?.name === 'Other' || data.custom_type);
    
    // Check if Custom Carrier should be shown
    const carrierId = data.policyCarrier_id || data.policy_carrier_id;
    const carrierObj = carriers.find(c => c.id === carrierId);
    setShowCustomCarrier(carrierObj?.name === 'Other' || data.custom_carrier);
    
    // Check if Pending Follow-up should be shown
    const statusId = data.policyStatus_id || data.policy_status_id;
    const statusObj = statuses.find(s => s.id === statusId);
    const isFollowUpStatus = statusObj?.name === 'Follow Up';
    const isCallXOrVIPA = ['CallX', 'VIPA Calls'].includes(sourceObj?.name);
    setShowPendingFollowUp((isFollowUpStatus && isCallXOrVIPA) || data.pending_follow_up);
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Handle Contact Source change
      if (field === 'contact_source_id') {
        const sourceObj = contactSources.find(s => s.id === value);
        updated.contact_source_name = sourceObj?.name || '';
        
        if (sourceObj?.name === 'Other') {
          setShowCustomSource(true);
        } else {
          setShowCustomSource(false);
          updated.custom_source = null;
        }
        
        // Check Pending Follow-up visibility
        const isCallXOrVIPA = ['CallX', 'VIPA Calls'].includes(sourceObj?.name);
        const statusObj = statuses.find(s => s.id === updated.policy_status_id);
        const isFollowUpStatus = statusObj?.name === 'Follow Up';
        setShowPendingFollowUp(isFollowUpStatus && isCallXOrVIPA);
      }
      
      // Handle Contact Type change
      if (field === 'contact_type_id') {
        const typeObj = contactTypes.find(t => t.id === value);
        updated.contact_type_name = typeObj?.name || '';
        
        if (typeObj?.name === 'Other') {
          setShowCustomType(true);
        } else {
          setShowCustomType(false);
          updated.custom_type = null;
        }
      }
      
      // Handle Carrier change
      if (field === 'policy_carrier_id') {
        const carrierObj = carriers.find(c => c.id === value);
        updated.carrier_name = carrierObj?.name || '';
        
        if (carrierObj?.name === 'Other') {
          setShowCustomCarrier(true);
        } else {
          setShowCustomCarrier(false);
          updated.custom_carrier = null;
        }
      }
      
      // Handle Status change
      if (field === 'policy_status_id') {
        const statusObj = statuses.find(s => s.id === value);
        updated.status_name = statusObj?.name || '';
        
        const isFollowUpStatus = statusObj?.name === 'Follow Up';
        const sourceObj = contactSources.find(s => s.id === updated.contact_source_id);
        const isCallXOrVIPA = ['CallX', 'VIPA Calls'].includes(sourceObj?.name);
        setShowPendingFollowUp(isFollowUpStatus && isCallXOrVIPA);
      }
      
      return updated;
    });
  };

  const handleSave = () => {
    // Validate required fields
    const validations = [
      { 
        check: formData.client, 
        message: 'Client Name is required' 
      },
      { 
        check: formData.state, 
        message: 'State is required' 
      },
      { 
        check: formData.contact_source_id || formData.contact_source_name, 
        message: 'Contact Source is required' 
      },
      { 
        check: formData.contact_type_id || formData.contact_type_name, 
        message: 'Contact Type is required' 
      },
      { 
        check: formData.policy_carrier_id || formData.carrier_name, 
        message: 'Carrier is required' 
      },
      { 
        check: formData.carrier_product, 
        message: 'Carrier Product is required' 
      },
      { 
        check: formData.initial_draft_date, 
        message: 'Initial Draft Date is required' 
      },
      { 
        check: formData.recurring_draft_day, 
        message: 'Recurring Draft Day is required' 
      },
      { 
        check: formData.monthly_premium, 
        message: 'Monthly Premium is required' 
      },
      { 
        check: formData.policy_status_id, 
        message: 'Status is required' 
      },
      { 
        check: formData.appointment_highlight, 
        message: 'Appointment Highlight is required' 
      }
    ];
    
    for (const { check, message } of validations) {
      if (!check) {
        toast.error(message);
        return;
      }
    }
    
    // Additional validation for conditional fields
    if (showCustomSource && !formData.custom_source) {
      toast.error('Custom Source is required when "Other" is selected');
      return;
    }
    
    if (showCustomType && !formData.custom_type) {
      toast.error('Custom Type is required when "Other" is selected');
      return;
    }
    
    if (showCustomCarrier && !formData.custom_carrier) {
      toast.error('Custom Carrier is required when "Other" is selected');
      return;
    }
    
    // Show confirmation modal
    setShowConfirmation(true);
  };

  const handleConfirmSave = async () => {
    setSaving(true);
    try {
      // Prepare payload matching the myPolicies response structure (without splits)
      const payload = {
        policy_id: policy.id,
        id: policy.id,
        client: formData.client,
        phone: formData.phone,
        state: formData.state,
        ghl_contactId: formData.ghl_contact_id || formData.ghl_contactId,
        ghl_locationId: formData.location_id || formData.ghl_locationId,
        ghl_locationName: formData.location_name || formData.ghl_locationName,
        ghl_opportunityId: formData.opportunity_id || formData.ghl_opportunityId,
        contactSource: formData.contact_source_name,
        contactSource_id: formData.contact_source_id || formData.contactSource_id,
        custom_source: formData.custom_source,
        contactType: formData.contact_type_name,
        contactType_id: formData.contact_type_id || formData.contactType_id,
        custom_type: formData.custom_type,
        policyStatus: formData.status_name,
        policyStatus_id: formData.policy_status_id || formData.policyStatus_id,
        policyCarrier: formData.carrier_name,
        policyCarrier_id: formData.policy_carrier_id || formData.policyCarrier_id,
        custom_carrier: formData.custom_carrier,
        carrier_product: formData.carrier_product,
        face_amount: formData.face_amount,
        beneficiary: formData.beneficiary,
        initial_draft_date: formData.initial_draft_date,
        recurring_draft_day: formData.recurring_draft_day,
        monthly_premium: formData.monthly_premium,
        annual_premium: formData.annual_premium,
        policy_number: formData.policy_number,
        agentOwner_name: formData.policyAgent_owner || formData.agentOwner_name,
        submittedBy_name: formData.submittedBy_name,
        pending_follow_up: formData.pending_follow_up,
        appointment_highlight: formData.appointment_highlight,
        isLocked: formData.isLocked,
        created_at: formData.created_at || formData.created_timestamp
      };
      
      // Call API to update policy
      const response = await axios.post(`${API}/portal/policy/update`, payload);
      
      if (response.data.success) {
        toast.success('Policy updated successfully!');
        onSave(formData);
        onOpenChange(false);
        setShowConfirmation(false);
      } else {
        toast.error('Failed to update policy');
      }
    } catch (error) {
      console.error('Error updating policy:', error);
      toast.error(error.response?.data?.message || 'Failed to update policy');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]" data-testid="edit-policy-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl">Edit Policy</DialogTitle>
          <DialogDescription>
            Update policy information for {policy?.client}
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-6">
            {/* Client Information */}
            <div className="space-y-3">
              <h3 className="font-semibold text-neutral-800 text-lg">Client Information</h3>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Client Name"
                  value={formData.client}
                  onChange={(v) => handleChange('client', v)}
                  required
                />
                <FormField
                  label="Phone"
                  value={formData.phone}
                  onChange={(v) => handleChange('phone', v)}
                  disabled={!!formData.ghl_contact_id}
                  required
                />
                <SearchableSelect
                  label="State"
                  value={formData.state}
                  onChange={(v) => handleChange('state', v)}
                  options={US_STATES}
                  open={stateOpen}
                  onOpenChange={setStateOpen}
                  required
                />
                <FormField
                  label="GHL Contact ID"
                  value={formData.ghl_contact_id}
                  disabled
                />
              </div>
            </div>

            {/* Contact Details */}
            <div className="space-y-3">
              <h3 className="font-semibold text-neutral-800 text-lg">Contact Details</h3>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <SearchableSelect
                  label="Contact Source"
                  value={formData.contact_source_id || formData.contactSource_id}
                  onChange={(v) => {
                    const sourceObj = contactSources.find(s => s.id === v);
                    handleChange('contact_source_id', v);
                    handleChange('contact_source_name', sourceObj?.name);
                  }}
                  options={contactSources.map(s => ({ value: s.id, label: s.name }))}
                  open={sourceOpen}
                  onOpenChange={setSourceOpen}
                  required
                />
                {showCustomSource && (
                  <FormField
                    label="Custom Source"
                    value={formData.custom_source}
                    onChange={(v) => handleChange('custom_source', v)}
                    required
                  />
                )}
                <SearchableSelect
                  label="Contact Type"
                  value={formData.contact_type_id || formData.contactType_id}
                  onChange={(v) => {
                    const typeObj = contactTypes.find(t => t.id === v);
                    handleChange('contact_type_id', v);
                    handleChange('contact_type_name', typeObj?.name);
                  }}
                  options={contactTypes.map(t => ({ value: t.id, label: t.name }))}
                  open={typeOpen}
                  onOpenChange={setTypeOpen}
                  required
                />
                {showCustomType && (
                  <FormField
                    label="Custom Type"
                    value={formData.custom_type}
                    onChange={(v) => handleChange('custom_type', v)}
                    required
                  />
                )}
              </div>
            </div>

            {/* Policy Details */}
            <div className="space-y-3">
              <h3 className="font-semibold text-neutral-800 text-lg">Policy Details</h3>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Policy Number"
                  value={formData.policy_number}
                  onChange={(v) => handleChange('policy_number', v)}
                  required
                />
                <SearchableSelect
                  label="Carrier Name"
                  value={formData.policy_carrier_id || formData.policyCarrier_id}
                  onChange={(v) => {
                    const carrierObj = carriers.find(c => c.id === v);
                    handleChange('policy_carrier_id', v);
                    handleChange('carrier_name', carrierObj?.name);
                  }}
                  options={carriers.map(c => ({ value: c.id, label: c.name }))}
                  open={carrierOpen}
                  onOpenChange={setCarrierOpen}
                  required
                />
                {showCustomCarrier && (
                  <FormField
                    label="Custom Carrier"
                    value={formData.custom_carrier}
                    onChange={(v) => handleChange('custom_carrier', v)}
                    required
                  />
                )}
                <FormField
                  label="Carrier Product"
                  value={formData.carrier_product}
                  onChange={(v) => handleChange('carrier_product', v)}
                  required
                />
                <FormField
                  label="Face Amount"
                  value={formData.face_amount}
                  onChange={(v) => handleChange('face_amount', v)}
                  type="number"
                />
                <FormField
                  label="Beneficiary"
                  value={formData.beneficiary}
                  onChange={(v) => handleChange('beneficiary', v)}
                />
              </div>
            </div>

            {/* Dates & Premium */}
            <div className="space-y-3">
              <h3 className="font-semibold text-neutral-800 text-lg">Dates & Premium</h3>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Initial Draft Date"
                  value={formData.initial_draft_date}
                  onChange={(v) => handleChange('initial_draft_date', v)}
                  type="date"
                  required
                />
                <FormField
                  label="Recurring Draft Day"
                  value={formData.recurring_draft_day}
                  onChange={(v) => handleChange('recurring_draft_day', v)}
                  type="number"
                  required
                />
                <FormField
                  label="Monthly Premium"
                  value={formData.monthly_premium}
                  onChange={(v) => handleChange('monthly_premium', v)}
                  type="number"
                  step="0.01"
                  required
                />
                <FormField
                  label="Annual Premium (Auto-calculated)"
                  value={formData.annual_premium}
                  disabled
                />
              </div>
            </div>

            {/* Status & Follow-up */}
            <div className="space-y-3">
              <h3 className="font-semibold text-neutral-800 text-lg">Status & Follow-up</h3>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <SearchableSelect
                  label="Status"
                  value={formData.policy_status_id || formData.policyStatus_id}
                  onChange={(v) => {
                    const statusObj = statuses.find(s => s.id === v);
                    handleChange('policy_status_id', v);
                    handleChange('status_name', statusObj?.name);
                  }}
                  options={statuses.map(s => ({ value: s.id, label: s.name }))}
                  open={statusOpen}
                  onOpenChange={setStatusOpen}
                  required
                />
                {showPendingFollowUp && (
                  <div className="col-span-2">
                    <FormTextarea
                      label="Pending Follow-up"
                      value={formData.pending_follow_up}
                      onChange={(v) => handleChange('pending_follow_up', v)}
                    />
                  </div>
                )}
                <div className="col-span-2">
                  <FormTextarea
                    label="Appointment Highlight"
                    value={formData.appointment_highlight}
                    onChange={(v) => handleChange('appointment_highlight', v)}
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-edit-button">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="bg-[#d4af37] hover:bg-[#b8941e] text-white"
            data-testid="save-policy-button"
          >
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">Confirm Policy Update</DialogTitle>
            <DialogDescription>
              Please review the changes before saving
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <SummaryField label="Client Name" value={formData.client} />
                <SummaryField label="Phone" value={formData.phone} />
                <SummaryField label="State" value={formData.state} />
                <SummaryField label="Contact Source" value={formData.contact_source_name} />
                <SummaryField label="Contact Type" value={formData.contact_type_name} />
                <SummaryField label="Status" value={formData.status_name} />
                <SummaryField label="Carrier" value={formData.carrier_name} />
                <SummaryField label="Carrier Product" value={formData.carrier_product} />
                <SummaryField label="Initial Draft Date" value={formData.initial_draft_date} />
                <SummaryField label="Recurring Draft Day" value={formData.recurring_draft_day} />
                <SummaryField label="Monthly Premium" value={formData.monthly_premium ? `$${formData.monthly_premium}` : ''} />
                <SummaryField label="Annual Premium" value={formData.annual_premium ? `$${formData.annual_premium}` : ''} />
              </div>
              
              {formData.pending_follow_up && (
                <div className="col-span-2">
                  <SummaryField label="Pending Follow-up" value={formData.pending_follow_up} fullWidth />
                </div>
              )}
              
              {formData.appointment_highlight && (
                <div className="col-span-2">
                  <SummaryField label="Appointment Highlight" value={formData.appointment_highlight} fullWidth />
                </div>
              )}
            </div>
          </ScrollArea>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmation(false)} disabled={saving}>
              Go Back
            </Button>
            <Button
              onClick={handleConfirmSave}
              disabled={saving}
              className="bg-[#d4af37] hover:bg-[#b8941e] text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Confirm & Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

// Helper Components
function FormField({ label, value, onChange, type = "text", disabled = false, required = false, step }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-neutral-700">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <Input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="border-neutral-300 focus:border-[#d4af37]"
        step={step}
      />
    </div>
  );
}

function FormTextarea({ label, value, onChange, required = false }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-neutral-700">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <Textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="border-neutral-300 focus:border-[#d4af37]"
        rows={3}
      />
    </div>
  );
}

function SearchableSelect({ label, value, onChange, options, open, onOpenChange, required = false }) {
  const [searchValue, setSearchValue] = useState('');
  
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-neutral-700">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between border-neutral-300"
          >
            {value ? options.find(opt => opt.value === value)?.label || value : `Select ${label}...`}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <div className="flex flex-col">
            {/* Search Input */}
            <div className="flex items-center border-b px-3">
              <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={`Search ${label}...`}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </div>
            
            {/* Options List */}
            <div className="max-h-64 overflow-auto p-1">
              {filteredOptions.length === 0 ? (
                <div className="py-6 text-center text-sm text-neutral-500">
                  No option found.
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={cn(
                      "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200",
                      value === option.value ? "bg-neutral-100" : ""
                    )}
                    onClick={() => {
                      onChange(option.value);
                      setSearchValue('');
                      onOpenChange(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === option.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {option.label}
                  </div>
                ))
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function SummaryField({ label, value, fullWidth = false }) {
  return (
    <div className={fullWidth ? "col-span-2" : ""}>
      <p className="text-xs text-neutral-500 mb-1">{label}</p>
      <p className="text-sm font-medium text-neutral-800">{value || 'N/A'}</p>
    </div>
  );
}
